/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";
import { TouchableOpacity, View } from "react-native";
import { useMutation } from "@apollo/client";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import {
  ButtonType,
  FadeInOutView,
  IssueModal,
  Text,
  UpduoButton,
} from "UpduoLibrary";
import {
  skaleBlack,
  skaleWhite,
  skaleDeactiveGray,
  upduoPrimaryBlue,
} from "config/UpduoColors";
import {
  ResultRatingCategoryType,
  SubmitAnswersMutation,
  SubmitFeedbackMutation,
} from "graphql/internal/generated";
import { SUBMIT_ANSWERS, SUBMIT_FEEDBACK } from "graphql/internal/Mutations";
import FiveStarRating from "./FiveStarRating";
import ChoiceRating from "./ChoiceRating";

import * as UpduoAnalytics from "config/UpduoAnalytics";

interface SessionFinishProps {
  onFinish: Function;
}

function QuestionOption({
  text,
  isSelected,
  onPressed,
}: {
  text: string;
  isSelected?: boolean;
  onPressed?: () => void;
}) {
  return (
    <TouchableOpacity
      style={{
        flex: 1,
        borderColor: isSelected ? upduoPrimaryBlue : skaleDeactiveGray,
        borderWidth: 2,
        marginBottom: 16,
        borderRadius: 4,
        padding: 8,
      }}
      onPress={onPressed}
    >
      <Text>{text}</Text>
    </TouchableOpacity>
  );
}

export default function SessionFinish({ onFinish }: SessionFinishProps) {
  const dispatch = useDispatch();

  const selfObject = useSelector((state: RootState) => state.selfObject);
  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );
  const skaleSessionObject = useSelector(
    (state: RootState) => state.skaleSessionObject
  );
  const isLearner = selfObject.userId === skaleSessionObject.learnerId;

  const [qualityRating, setQualityRating] = useState(0);
  const [resultRating, setResultRating] =
    useState<ResultRatingCategoryType | null>(null);

  // Specific end-of-session questions for Comfort Systems organization
  const [effectiveRating, setEffectiveRating] = useState(0);
  const [skillsRating, setSkillsRating] = useState<string[]>([]);

  // Specific end-of-session questions for AstraZeneca organization
  const questionsTeacherAZ = [
    {
      type: "linear_scale",
      lowText: "Not at all confident",
      highText: "Extremely confident",
      question:
        "My current confidence level in holding coaching and feedforward dialogues.",
    },
    {
      type: "linear_scale",
      lowText: "Not at all skilled",
      highText: "Extremely skilled",
      question: "My current skill level in coaching and feedforward.",
    },
    {
      type: "linear_scale",
      lowText: "Not at all effective",
      highText: "Extremely effective",
      question:
        "How effective overall was this coaching and feedforward dialogue?",
    },
  ];
  const questionsLearnerAZ = [
    {
      type: "linear_scale",
      lowText: "Not at all confident",
      highText: "Extremely confident",
      question:
        "Assessment of coach’s confidence level in holding coaching and feedforward dialogues.",
    },
    {
      type: "linear_scale",
      lowText: "Not at all skilled",
      highText: "Extremely skilled",
      question:
        "Assessment of coach’s skill level in coaching and feedforward.",
    },
    {
      type: "linear_scale",
      lowText: "Not at all effective",
      highText: "Extremely effective",
      question:
        "How effective overall was this coaching and feedforward dialogue?",
    },
  ];
  const questionsAZ = isLearner ? questionsLearnerAZ : questionsTeacherAZ;
  const [answerAZ1, setAnswerAZ1] = useState(0);
  const [answerAZ2, setAnswerAZ2] = useState(0);
  const [answerAZ3, setAnswerAZ3] = useState(0);

  const [isIssueModalVisible, setIsIssueModalVisible] = useState(false);

  const [submitAnswers, {}] = useMutation<SubmitAnswersMutation>(
    SUBMIT_ANSWERS,
    {
      onCompleted: () => {
        _handleAnswersResult(true);
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-session-finish-submitanswers-error", {
          error,
        });
        console.error("ERROR submitAnswers", error);
      },
    }
  );

  const [submitFeedback, {}] = useMutation<SubmitFeedbackMutation>(
    SUBMIT_FEEDBACK,
    {
      onCompleted: () => {
        _handleFeedbackResult(true);
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-session-finish-submitfeedback-error", {
          error,
        });
        console.error("ERROR submitFeedback", error);
      },
    }
  );

  useEffect(() => {
    // Execute after mounting
    dispatch(ActionCreators.setIsPostEvent(true));
  }, [dispatch, selectedSummit]);

  const _handleInitiateReturnHome = useCallback(() => {
    // Ensure that user does not re-enter queue upon returning to HomeScreen
    dispatch(ActionCreators.setIsQueueBeaconActivated(false));

    if (selfObject?.organizationId === "T3JnYW5pemF0aW9uVHlwZTo1MjI=") {
      // Specific end-of-session questions for Comfort Systems organization
      const payload = {
        effectiveRating,
        skillsRating,
      };
      UpduoAnalytics.logEvent("user-session-finish-feedback-submit", payload);
      submitFeedback({
        variables: {
          submitFeedbackRequest: {
            skalesessionId: skaleSessionObject?.id,
            content: JSON.stringify(payload),
            role:
              selfObject.userId === skaleSessionObject?.learnerId
                ? "LEARNER"
                : "TEACHER",
          },
        },
      });
    } else if (selfObject?.organizationId === "T3JnYW5pemF0aW9uVHlwZTo0OTc=") {
      // Specific end-of-session questions for AstraZeneca organization
      const payload = questionsAZ.map((question, index) => {
        return {
          type: question.type,
          context: `${question.lowText} | ${question.highText}`,
          question: question.question,
          answer: [answerAZ1, answerAZ2, answerAZ3][index],
        };
      });
      UpduoAnalytics.logEvent("user-session-finish-answers-submit", payload);
      submitAnswers({
        variables: {
          submitAnswersRequest: {
            skalesessionId: skaleSessionObject?.id,
            answers: payload,
          },
        },
      });
    } else if (qualityRating !== 0 || resultRating !== null) {
      UpduoAnalytics.logEvent("user-session-finish-feedback-submit", {
        qualityRating,
        resultRating,
      });
      submitFeedback({
        variables: {
          submitFeedbackRequest: {
            skalesessionId: skaleSessionObject?.id,
            qualityRating,
            resultRating,
            role:
              selfObject.userId === skaleSessionObject?.learnerId
                ? "LEARNER"
                : "TEACHER",
          },
        },
      });
    } else {
      _handleReturnHome();
    }
    UpduoAnalytics.logEvent("user-event-done");
  }, [
    qualityRating,
    resultRating,
    effectiveRating,
    skillsRating,
    answerAZ1,
    answerAZ2,
    answerAZ3,
    selfObject,
    skaleSessionObject,
  ]);

  const _handleAnswersResult = (isSuccessful: boolean) => {
    // Reports feedback outcome to analytics, sends user back to Home
    const payload = questionsAZ.map((question, index) => {
      return {
        type: question.type,
        context: `${question.lowText} | ${question.highText}`,
        question: question.question,
        answer: [answerAZ1, answerAZ2, answerAZ3][index],
      };
    });
    UpduoAnalytics.logEvent("app-session-finish-answers-submit", {
      success: isSuccessful,
      answers: payload,
    });
    _handleReturnHome();
  };

  const _handleFeedbackResult = (isSuccessful: boolean) => {
    // Reports feedback outcome to analytics, sends user back to Home
    UpduoAnalytics.logEvent("app-session-finish-feedback-submit", {
      success: isSuccessful,
      qualityRating: qualityRating,
      resultRating: resultRating,
    });
    _handleReturnHome();
  };

  const _handleReturnHome = () => {
    UpduoAnalytics.logEvent("user-return-home");
    dispatch(ActionCreators.setPartnerObject({}));
    onFinish();
  };

  const questionOptions = [
    { text: "Taught", catagory: ResultRatingCategoryType.Taught },
    { text: "Learned", catagory: ResultRatingCategoryType.Learned },
    { text: "Both", catagory: ResultRatingCategoryType.Both },
    { text: "Neither", catagory: ResultRatingCategoryType.Neither },
  ];

  const skillsOptions = [
    { text: "Concise" },
    { text: "Confident" },
    { text: "Knowledgable" },
    { text: "Personable" },
    { text: "Persuasive" },
  ];

  // Specific end-of-session questions for Comfort Systems organization
  if (selfObject?.organizationId === "T3JnYW5pemF0aW9uVHlwZTo1MjI=") {
    return (
      <FadeInOutView
        style={{
          flex: 1,
          alignItems: "center",
        }}
      >
        <View
          style={{
            marginTop: 87,
            paddingTop: 32,
            padding: 30,
            backgroundColor: skaleWhite,
            borderRadius: 12,
            width: 660,
          }}
        >
          <Text
            isHeading={true}
            style={{
              fontFamily: "ObjectSans-Medium",
              fontSize: 18,
              color: skaleBlack,
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            {"Post-Exercise Questions"}
          </Text>
          <Text
            style={{
              fontFamily: "ObjectSans-Medium",
              fontSize: 16,
              color: skaleBlack,
              marginVertical: 20,
            }}
          >
            {
              "Sales skills demonstrated during this cold call (select all that apply):"
            }
          </Text>
          {skillsOptions.map((option, index) => {
            return (
              <QuestionOption
                key={index}
                text={option.text}
                isSelected={skillsRating.includes(option.text)}
                onPressed={() => {
                  if (skillsRating.includes(option.text)) {
                    setSkillsRating(
                      skillsRating.filter((text) => text !== option.text)
                    );
                  } else {
                    setSkillsRating([...skillsRating, option.text]);
                  }
                }}
              />
            );
          })}
          <Text
            style={{
              fontFamily: "ObjectSans-Medium",
              fontSize: 16,
              color: skaleBlack,
              marginVertical: 20,
            }}
          >
            {"How effective overall was this cold call?"}
          </Text>
          <ChoiceRating
            rating={effectiveRating}
            onChange={setEffectiveRating}
          />
          <View
            style={{
              flex: 1,
              width: "100%",
              marginTop: 20,
              marginBottom: 30,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <UpduoButton
              disabled={effectiveRating === 0}
              onPress={_handleInitiateReturnHome}
              text={"Done"}
              type={ButtonType.SECONDARY}
              style={{
                width: 295,
                height: 65,
                borderRadius: 65 / 2,
                justifyContent: "center",
              }}
            />
          </View>
          <TouchableOpacity
            style={{ position: "absolute", bottom: 20, right: 0 }}
            onPress={() => {
              setIsIssueModalVisible(true);
            }}
          >
            <Text
              style={{
                fontSize: 16,
                color: upduoPrimaryBlue,
                textAlign: "center",
                paddingHorizontal: 30,
                paddingVertical: 6,
              }}
            >
              {"Report an Issue"}
            </Text>
          </TouchableOpacity>
        </View>
        <IssueModal
          sessionId={skaleSessionObject?.id}
          isVisible={isIssueModalVisible}
          onDismiss={() => {
            setIsIssueModalVisible(false);
          }}
        />
      </FadeInOutView>
    );
  }

  // Specific end-of-session questions for AstraZeneca organization
  if (selfObject?.organizationId === "T3JnYW5pemF0aW9uVHlwZTo0OTc=") {
    return (
      <FadeInOutView
        style={{
          flex: 1,
          alignItems: "center",
        }}
      >
        <View
          style={{
            marginTop: 87,
            paddingTop: 32,
            padding: 30,
            backgroundColor: skaleWhite,
            borderRadius: 12,
            width: 660,
          }}
        >
          <Text
            isHeading={true}
            style={{
              fontFamily: "ObjectSans-Medium",
              fontSize: 18,
              color: skaleBlack,
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            {"Post-Exercise Questions"}
          </Text>
          {questionsAZ.map((question, index) => {
            return (
              <View key={index}>
                <Text
                  style={{
                    fontFamily: "ObjectSans-Medium",
                    fontSize: 16,
                    color: skaleBlack,
                    marginVertical: 20,
                  }}
                >
                  {question.question}
                </Text>
                <ChoiceRating
                  lowText={question.lowText}
                  highText={question.highText}
                  rating={[answerAZ1, answerAZ2, answerAZ3][index]}
                  onChange={(rating) => {
                    switch (index) {
                      case 0:
                        setAnswerAZ1(rating);
                        break;
                      case 1:
                        setAnswerAZ2(rating);
                        break;
                      case 2:
                        setAnswerAZ3(rating);
                        break;
                    }
                  }}
                />
              </View>
            );
          })}
          <View
            style={{
              flex: 1,
              width: "100%",
              marginTop: 20,
              marginBottom: 30,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <UpduoButton
              disabled={answerAZ1 === 0 || answerAZ2 === 0 || answerAZ3 === 0}
              onPress={_handleInitiateReturnHome}
              text={"Done"}
              type={ButtonType.SECONDARY}
              style={{
                width: 295,
                height: 65,
                borderRadius: 65 / 2,
                justifyContent: "center",
              }}
            />
          </View>
          <TouchableOpacity
            style={{ position: "absolute", bottom: 20, right: 0 }}
            onPress={() => {
              setIsIssueModalVisible(true);
            }}
          >
            <Text
              style={{
                fontSize: 16,
                color: upduoPrimaryBlue,
                textAlign: "center",
                paddingHorizontal: 30,
                paddingVertical: 6,
              }}
            >
              {"Report an Issue"}
            </Text>
          </TouchableOpacity>
        </View>
        <IssueModal
          sessionId={skaleSessionObject?.id}
          isVisible={isIssueModalVisible}
          onDismiss={() => {
            setIsIssueModalVisible(false);
          }}
        />
      </FadeInOutView>
    );
  }

  return (
    <FadeInOutView
      style={{
        flex: 1,
        alignItems: "center",
        marginBottom: 100,
      }}
    >
      <View
        style={{
          marginTop: 87,
          paddingTop: 32,
          padding: 30,
          backgroundColor: skaleWhite,
          borderRadius: 12,
          width: 660,
        }}
      >
        <Text
          isHeading={true}
          style={{
            fontFamily: "ObjectSans-Medium",
            fontSize: 18,
            color: skaleBlack,
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          {"Session Feedback"}
        </Text>
        <Text
          style={{
            fontFamily: "ObjectSans-Medium",
            fontSize: 16,
            color: skaleBlack,
            marginVertical: 20,
          }}
        >
          {"How was the quality of the call?"}
        </Text>
        <FiveStarRating rating={qualityRating} onChange={setQualityRating} />
        <Text
          isHeading={true}
          style={{
            fontSize: 14,
            color: skaleBlack,
            marginTop: 15,
            marginBottom: 10,
          }}
        >
          {"Did you teach or learn something?"}
        </Text>
        {questionOptions.map((option, index) => {
          return (
            <QuestionOption
              key={index}
              text={option.text}
              isSelected={resultRating === option.catagory}
              onPressed={() => {
                setResultRating(option.catagory);
              }}
            />
          );
        })}
        <View
          style={{
            flex: 1,
            width: "100%",
            marginTop: 20,
            marginBottom: 30,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <UpduoButton
            onPress={_handleInitiateReturnHome}
            text={"Done"}
            type={ButtonType.SECONDARY}
            style={{
              width: 295,
              height: 65,
              borderRadius: 65 / 2,
              justifyContent: "center",
            }}
          />
        </View>
        <TouchableOpacity
          style={{ position: "absolute", bottom: 20, right: 0 }}
          onPress={() => {
            setIsIssueModalVisible(true);
          }}
        >
          <Text
            style={{
              fontSize: 16,
              color: upduoPrimaryBlue,
              textAlign: "center",
              paddingHorizontal: 30,
              paddingVertical: 6,
            }}
          >
            {"Report an Issue"}
          </Text>
        </TouchableOpacity>
      </View>
      <IssueModal
        sessionId={skaleSessionObject?.id}
        isVisible={isIssueModalVisible}
        onDismiss={() => {
          setIsIssueModalVisible(false);
        }}
      />
    </FadeInOutView>
  );
}
